<template>
    <div class="patrimony-tab-content">
        <!-- Slider -->
        <slider-generic v-if="parsedList.length" :sliderList="parsedList">
            <div
            v-for="currency in parsedList"
            :key="`currency${currency.currency}`"
            class="currency-item">
                <div class="info-block">
                    <div class="chart-container">
                        <div class="percentage-label">{{currency.assetsPercentage !== 0 && currency.assetsPercentage === 0 ? '≈ ' : ''}}{{currency.assetsPercentage}}%</div>
                        <!-- Chart -->
                        <chart-simple
                        :chartId="`chart${currency.currency}Assets`"
                        :graphData="currency.assetsPercentageChart"
                        :colorsArray="getAppThemeMode === 'light' ? ['#00894E', '#EDEDED'] : ['#5E977E', '#DDDDDE']"
                        :pieHole="0.8"/>
                    </div>
                    <!-- Amounts -->
                    <div class="text-wrapper">
                        <div class="title">{{t('home.widgets.patrimony.labels.totalsAssets')}}</div>
                        <div class="amount" :key="hideAmounts">{{amountFormat(currency.totalAssets, currency.currency, null, null, null, hideAmounts)}}</div>
                    </div>
                </div>
                <div class="info-block">
                    <div class="chart-container">
                        <div class="percentage-label">{{currency.liabilitiesPercentage !== 0 && currency.liabilitiesPercentage === 0 ? '≈ ' : ''}}{{currency.liabilitiesPercentage}}%</div>
                        <!-- Chart -->
                        <chart-simple
                        :chartId="`chart${currency.currency}Liabilities`"
                        :graphData="currency.liabilitiesPercentageChart"
                        :colorsArray="getAppThemeMode === 'light' ? ['#C7001B', '#EDEDED'] : ['#BD7F88', '#DDDDDE']"
                        :pieHole="0.8"/>
                    </div>
                    <!-- Amounts -->
                    <div class="text-wrapper">
                        <div class="title">{{t('home.widgets.patrimony.labels.totalsLiabilities')}}</div>
                        <div class="amount" :key="hideAmounts">{{amountFormat(currency.totalLiabilities, currency.currency, null, null, null, hideAmounts)}}</div>
                    </div>
                </div>
            </div>
        </slider-generic>
    </div>
</template>

<script setup>
import SliderGeneric from '@/components/generic/sliders/Generic'
import ChartSimple from '@/components/generic/charts/Simple'

/* Vue & Assets */
import { computed } from 'vue'
import { amountFormat } from '@/assets/scripts/amounts'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapGetters */
const { getPatrimonyInfo } = mapGetters(moduleMapper.HOME)
const { getAppThemeMode } = mapGetters()

const props = defineProps({
    hideAmounts: Boolean
})

const parsedList = computed(() => {
    const parsedInfo = getPatrimonyInfo.value.currenciesFromAccounts.map(currency => {
        const totalAssets = getPatrimonyInfo.value.totalAssetsAvailableBalanceByCurrency[currency.name] || 0
        const totalLiabilities = Math.abs(getPatrimonyInfo.value.totalLiabilitiesAvailableBalanceByCurrency[currency.name] || 0)
        const totalMoney = totalAssets + totalLiabilities
        const assetsPercentage = totalMoney === 0 ? 0 : Math.round(((totalAssets * 100) / totalMoney * 10)) / 10
        const liabilitiesPercentage = totalMoney === 0 ? 0 : Math.round(((totalLiabilities * 100) / totalMoney * 10)) / 10

        return {
            currency: currency.name,
            totalAssets: totalAssets,
            assetsPercentage: assetsPercentage,
            assetsPercentageChart: [
                ['assets', assetsPercentage],
                ['liabilities', totalMoney === 0 ? 100 : liabilitiesPercentage]
            ],
            totalLiabilities: totalLiabilities,
            liabilitiesPercentage: liabilitiesPercentage,
            liabilitiesPercentageChart: [
                ['liabilities', liabilitiesPercentage],
                ['assets', totalMoney === 0 ? 100 : assetsPercentage]
            ]
        }
    })

    return parsedInfo
})
</script>

<style lang="scss" scoped>
.patrimony-tab-content {
    width: 100%;
    height: 180px;
    padding: 16px 0 0 0;

    :deep(.slider-content-wrapper) .slider-wrapper {
        padding: 16px 16px 0 16px;
    }

    .currency-item {
        width: calc(100% + 24px);
        height: 122px;

        display: flex;
        flex-direction: column;
        flex-shrink: 0;
        gap: 32px;

        .info-block {
            width: 100%;

            display: flex;
            gap: 32px;

            .chart-container {
                width: 40px;
                height: 40px;
                position: relative;
                flex-shrink: 0;

                .percentage-label {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);

                    @include H9-Regular;
                    color: var(--secondary-dark-2);

                    z-index: 1;
                }
            }

            .text-wrapper {
                display: flex;
                flex-direction: column;
                gap: 8px;

                .title {
                    @include H6-Regular;
                    color: var(--secondary-dark-3);
                }

                .amount {
                    @include H4-Medium;
                    color: var(--secondary-dark-3);
                }
            }
        }
    }
}
</style>
